/**
 * Constants and presets for the website builder.
 *
 * The document schema treats these as data: a theme is a preset id, never a
 * colour or a font name typed by the owner. That is the whole point of §2 of
 * the brief ("nie da się zrobić brzydkiej strony") and it is also what keeps
 * CLAUDE.md §2 satisfied on the public side. The palettes below are the same
 * kind of documented exception as `features/employee-schedule/utils/colors.ts`:
 * a closed set, in one file, chosen by a designer rather than by a user.
 */

/**
 * Seam between a websites API and the local persisted store, exactly as
 * `features/floor-plan`, `features/loyalty` and `features/feedback` use it.
 *
 * WHY IT IS OFF: `menu_core_be` has no `websites` domain. The pulled spec in
 * `src/api/core/api.json` carries 95 paths and none of them mentions a page, a
 * site or a document.
 *
 * This is scaffolding, not a shippable state. A website builder whose documents
 * live in localStorage cannot go to production, and PLAN.md §4 says so: phase 2
 * does not close without the backend domain.
 */
export const WEBSITE_USE_API = false

/** Backend path, relative to `/restaurants/{restaurantId}`. */
export const WEBSITE_ENDPOINT = '/websites'

/**
 * Where a booking form on a published page posts, relative to the site's own
 * origin, with the city and venue slugs appended (`render/urls.ts`).
 *
 * A seam, not a working route. `menu_core_be` has no `reservations` domain
 * (PLAN.md §5, phase 4) and nothing on the server answers here yet; the form is
 * plain HTML so that when the domain exists it is one route and no change to a
 * published page. Under `/api` so it can never collide with a page slug an
 * owner picks, since pages live directly under the venue's path.
 */
export const RESERVATION_ACTION_PATH = '/api/reservations'

/**
 * The most people one booking may be for, until the owner sets otherwise.
 *
 * A table for twelve is a party the venue wants to hear about first, which is
 * what the telephone under the form is for.
 */
export const DEFAULT_MAX_GUESTS = 10

/**
 * Schema version of documents this build writes.
 *
 * Bump it together with a migration in `migrations/registry.ts`. A published
 * page saved at an older version must survive, which is what the runner in
 * `migrations/` exists for.
 */
export const DOCUMENT_VERSION = 1

/*
  Limits, enforced in the schema so they hold on the client, in the editor and
  on any endpoint that parses a document (brief §9). They are deliberately
  generous: they exist to stop a runaway loop or a paste bomb, not to shape what
  an owner may build.
*/
export const MAX_PAGES_PER_SITE = 20
export const MAX_ROWS_PER_PAGE = 60
export const MAX_ITEMS_PER_COLUMN = 12
export const MAX_GALLERY_IMAGES = 24

/*
  A room drawn on a public page. Well above any real dining room, and low enough
  that a plan copied into the document cannot make it enormous.
*/
export const MAX_TABLES_PER_ROOM = 200

/*
  How many rooms one block may show. A venue has a handful, a floor at most;
  the cap is here so a plan copied into the document cannot make it enormous.
*/
export const MAX_ROOMS_PER_BLOCK = 12

/**
 * The templates an owner picks from (`templates/registry.ts`). Ids are stored
 * in the document; a document naming one that no longer exists falls back to
 * the first.
 */
export const TEMPLATE_IDS = ['classic', 'photo', 'minimal'] as const
export type TemplateId = (typeof TEMPLATE_IDS)[number]

/*
  The owner's own blocks on a template page: a text, or a photo with a text,
  placed after a section of their choosing. Four is enough to say something the
  sections do not; more and the page stops being the template it was chosen
  for, which is what the free builder was and what this replaces.
*/
export const MAX_TEMPLATE_EXTRAS = 4

/** Photos in the template's gallery. Under `MAX_GALLERY_IMAGES` on purpose. */
export const MAX_TEMPLATE_GALLERY_PHOTOS = 12

/**
 * The free block builder (`/website/builder`) is switched off.
 *
 * Not deleted: the canvas, the palette, the properties panel and their tests
 * stay, and the store's undo and `customized` flag stay with them. The owner
 * decided a page assembled from free rows is more than a restaurant needs and
 * less than a designer wants; the template with its sections and own blocks is
 * the product, and bespoke pages are a service. Flip this to get the builder
 * back as an internal tool.
 */
export const WEBSITE_BUILDER_ENABLED = false
/** Longest single run of text in one language. Roughly ten screens of prose. */
export const MAX_TEXT_LENGTH = 20000

/**
 * How much translated text a language version needs before it may be published.
 *
 * PLAN.md §2.3: an English page that is 80% Polish is duplicate content, and at
 * thousands of venues on one domain that signal is shared by every customer.
 * The check is a share of translatable fields that are actually filled in.
 */
export const MIN_TRANSLATED_SHARE = 0.8

/*
  Before a page is allowed into Google it has to be a page, not a filled-in
  template. PLAN.md §2.2: thousands of venues share one domain, so a wall of
  near-identical starter pages is a doorway-page signal charged to every
  customer at once. Below these thresholds the page still publishes and still
  works as a link, it just carries `noindex`.

  The brief also asks for "at least one photo that is not from the template",
  which needs an origin flag on the asset. That flag arrives with the asset
  service; until then this counts photos.
*/
export const MIN_INDEXABLE_TEXT_CHARS = 400
export const MIN_INDEXABLE_IMAGES = 1

/**
 * What the colour picker opens on, and what the editor previews with when a
 * document predates the brand colour. One definition, because the settings form
 * and the row background picker have to agree about what they are showing.
 */
export const DEFAULT_BRAND_COLOR = '#b4552f'

/** Palettes an owner picks from. Ids are stored; the values never are. */
export const THEME_PALETTES = [
  'warm-sand',
  'olive-kitchen',
  'charcoal-bistro',
  'terracotta',
  'forest-green'
] as const

/**
 * Typographic pairs, heading font plus body font. Ids only, as above: the
 * families themselves live in `render/fonts.ts`.
 *
 * The first four ids are the ones documents were already stored with, so they
 * keep their meaning and nothing needs migrating. What changed underneath them
 * is the faces they name, which went from whatever the visitor's machine
 * happened to have to real ones.
 */
export const THEME_FONT_PAIRS = [
  'classic-serif',
  'modern-sans',
  'condensed-display',
  'friendly-rounded',
  'elegant-serif',
  'editorial-serif',
  'bistro-script',
  'warm-slab'
] as const
